import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "./LudConnect.css";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import { tx } from "./ludConnect.constants";
import RestaurantStrip from "./components/RestaurantStrip/RestaurantStrip";
import PortalStats from "./components/PortalStats/PortalStats";
import RestaurantSummary from "./components/RestaurantSummary/RestaurantSummary";
import AdminApps from "./components/AdminApps/AdminApps";
import Branches from "./components/Branches/Branches";
import MenuPortal from "./components/MenuPortal/MenuPortal";
import RequestsPanel from "./components/RequestsPanel/RequestsPanel";
import { APPS } from "./ludConnect.constants";
import { fmtDate } from "./helpers/ludConnect.helpers";
import { AlertTriangle, Button, NoticeBox, RefreshCcw, X } from "../ui";
import { Loading } from "../loading/Loading";
import {
  addCommentAPI,
  createRequestAPI,
  deleteRequestAPI,
  getOverviewAPI,
  restoreRequestAPI,
  transitionRequestAPI,
  updateBranchAPI,
  updateProfileAPI,
  upsertBranchAppAPI,
} from "../../utils/api/ludConnect";
import { getLudDocumentFileUrlAPI } from "../../utils/api/ludDocuments";
import { DeleteModal } from "../modals/Delete";
import RejectModal from "../modals/RejectModal";
import LudConnectFormModal from "../modals/LudConnectFormModal";
import FilterUI from "../analysis/filter/FilterUI";

// Text fields autosave; wait for a pause in typing instead of calling the API
// (and reloading the whole overview) on every keystroke.
const AUTOSAVE_DELAY = 600;

const clone = (value) =>
  typeof structuredClone === "function"
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value));

// Field edits only touch profile/branch data; don't deep-copy the (large) menu.
const cloneRestaurant = ({ menu, ...rest }) => ({ ...clone(rest), menu });

// Stable fallback so memoised values depending on `menu` don't recompute every render.
const EMPTY_MENU = { sections: [] };

const BASIC_FILTER_DATA = {
  restaurant: [],
  section: [],
  item: [],
};

const sortByNewestCreatedAt = (items = []) =>
  [...items].sort((first, second) => {
    const firstTime = new Date(
      first.createdAt || first.updatedAt || first.deletedAt || 0,
    ).getTime();
    const secondTime = new Date(
      second.createdAt || second.updatedAt || second.deletedAt || 0,
    ).getTime();

    return (
      (Number.isNaN(secondTime) ? 0 : secondTime) -
      (Number.isNaN(firstTime) ? 0 : firstTime)
    );
  });

// The portal shows the LIVE content of every active menu of the restaurant
// as one list; with several menus each section is prefixed by its menu name.
const normalizeMenu = (menus = [], restaurant = {}) => {
  const list = Array.isArray(menus) ? menus : menus ? [menus] : [];
  const multiple = list.length > 1;
  const rawSections = list.flatMap((menu) =>
    (menu.sections || []).map((section) => ({
      ...section,
      _id: `${menu.id || menu._id}:${section._id}`,
      name: multiple
        ? { ar: `${menu.name} · ${section.name?.ar || ""}`, en: `${menu.name} · ${section.name?.en || ""}` }
        : section.name,
    })),
  );
  const exportSource = {
    name: list.map((menu) => menu.name).join(" + ") || "Menu",
    restaurant: { businessName: restaurant.businessName || restaurant.name || restaurant.id || "Menu" },
    sections: rawSections,
  };

  return {
    id: list[0]?.id || list[0]?._id || restaurant.id,
    menuIds: list.map((menu) => menu.id || menu._id),
    exportSource,
    sections: rawSections.map((section, sectionIndex) => ({
      ...section,
      id: String(section._id || section.id || sectionIndex),
      name:
        typeof section.name === "object"
          ? section.name.ar || section.name.en || ""
          : section.name || "",
      items: (section.items || []).map((item, itemIndex) => ({
        ...item,
        sourceItem: item,
        id: String(item._id || item.id || itemIndex),
        name:
          typeof item.name === "object"
            ? item.name.ar || item.name.en || ""
            : item.name || "",
        desc:
          typeof item.description === "object"
            ? item.description.ar || item.description.en || ""
            : item.description || item.desc || "",
        // Backend-computed effective price of the default pricing entry.
        price: Number(item.effectivePrice ?? item.pricing?.[0]?.price ?? 0),
        cal: Number(
          typeof item.calories === "object"
            ? item.calories.ar || item.calories.en || 0
            : item.calories || item.cal || 0,
        ),
      })),
    })),
  };
};

const LudConnect = () => {
  const { lang = "ar" } = useContext(Context);
  const { can, has } = useAccess();
  const ar = lang === "ar";
  const token = localStorage.getItem("auth-token") || "";
  // Display only (role, no restaurant): which controls to show. Every handler
  // below re-checks with `allowedFor`, bound to the restaurant it targets.
  // integration partner (feedus) vs LUD employee: the two sides of a request
  const isProvider = has("ludConnect.provider.act");
  // LUD-side connection and menu actions (Branches / MenuPortal) are limited
  // to CEO-level roles; provider (feedus) actions are handled by RequestsPanel.
  const canManage = has("ludConnect.manage");
  const [loadState, setLoadState] = useState({ status: "loading", error: "" });
  const [notice, setNotice] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [menus, setMenus] = useState({});
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [activity, setActivity] = useState([]);
  const [formModal, setFormModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteState, setDeleteState] = useState({ loading: false, error: "" });
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [pendingActions, setPendingActions] = useState(() => new Set());
  const [filters, setFilters] = useState(BASIC_FILTER_DATA);

  const overviewSeq = useRef(0);
  const saveTimers = useRef(new Map());
  const savesInFlight = useRef(0);
  const hasPendingSaves = () =>
    saveTimers.current.size > 0 || savesInFlight.current > 0;

  const reportError = useCallback(
    (error, fallback) => {
      console.error(error);
      setNotice({
        variant: "danger",
        text: fallback || tx("تعذّر تنفيذ العملية", "The action failed", ar),
        detail: error?.message || "",
      });
    },
    [ar],
  );

  const handleFilterChange = (category, values) => {
    setFilters((prev) => ({
      ...prev,
      [category]: values,
    }));
  };

  const handleClearFilters = () => {
    setFilters(BASIC_FILTER_DATA);
  };

  const loadOverview = useCallback(async () => {
    const seq = ++overviewSeq.current;
    const response = await getOverviewAPI(token);
    // Ignore responses that were overtaken by a newer reload, and don't
    // overwrite fields the user is still editing (a later save reloads again).
    const editing = saveTimers.current.size > 0 || savesInFlight.current > 0;
    if (seq !== overviewSeq.current || editing) return;

    const nextRestaurants = response.data?.restaurants || [];
    const byId = new Map(nextRestaurants.map((item) => [item.id, item]));
    setRestaurants(nextRestaurants);
    setMenus(
      Object.fromEntries(
        nextRestaurants.map((item) => [
          item.id,
          normalizeMenu(item.menus, item),
        ]),
      ),
    );
    setRequests(
      sortByNewestCreatedAt(
        (response.data?.requests || []).map((request) => ({
          ...request,
          id: request._id || request.id,
          payload: {
            ...(request.payload || {}),
            restId: request.storeCode,
            restName:
              request.payload?.restName ||
              byId.get(request.storeCode)?.name ||
              request.storeCode,
          },
          comments: (request.comments || []).map((comment) => ({
            ...comment,
            actor: comment.actorName || comment.actor || "",
          })),
        })),
      ),
    );
    setActivity(
      sortByNewestCreatedAt(
        (response.data?.activities || []).map((item) => ({
          restId: item.storeCode,
          createdAt: item.createdAt,
          rawDate: item.createdAt,
          actor: item.actorName,
          action: item.action,
        })),
      ),
    );
    setSelectedId((current) =>
      nextRestaurants.some((item) => item.id === current)
        ? current
        : nextRestaurants[0]?.id || "",
    );
    setLoadState({ status: "ready", error: "" });
  }, [token]);

  const initialLoad = useCallback(() => {
    setLoadState({ status: "loading", error: "" });
    loadOverview().catch((error) => {
      console.error(error);
      setLoadState({ status: "error", error: error?.message || "" });
    });
  }, [loadOverview]);

  useEffect(() => {
    initialLoad();
  }, [initialLoad]);

  // Background refresh after a successful change; failures are reported, not swallowed.
  const refreshOverview = useCallback(
    () =>
      loadOverview().catch((error) =>
        reportError(
          error,
          tx("تعذّر تحديث البيانات", "Could not refresh the data", ar),
        ),
      ),
    [loadOverview, reportError, ar],
  );

  // Flush unsaved text edits if the page is left before the autosave fires.
  useEffect(() => {
    const timers = saveTimers.current;
    return () => {
      timers.forEach(({ timer, save }) => {
        clearTimeout(timer);
        save().catch((error) => console.error(error));
      });
      timers.clear();
    };
  }, []);

  const scheduleSave = (key, save) => {
    const pending = saveTimers.current.get(key);
    if (pending) clearTimeout(pending.timer);
    const timer = setTimeout(async () => {
      saveTimers.current.delete(key);
      savesInFlight.current += 1;
      try {
        await save();
      } catch (error) {
        reportError(
          error,
          tx("لم يتم حفظ التعديل", "The change was not saved", ar),
        );
      } finally {
        savesInFlight.current -= 1;
      }
      // Re-sync once typing has settled (also reverts a failed optimistic edit).
      if (!hasPendingSaves()) refreshOverview();
    }, AUTOSAVE_DELAY);
    saveTimers.current.set(key, { timer, save });
  };

  // Guards one-click actions (link, add app) against duplicate submissions.
  const runAction = async (key, action) => {
    if (pendingActions.has(key)) return;
    setPendingActions((current) => new Set(current).add(key));
    try {
      await action();
    } catch (error) {
      reportError(error);
    } finally {
      setPendingActions((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    }
  };

  // Execution-time authorization (NA-010 / NA-011). The restaurant must be one the
  // overview returned (a verified public id, never a guess or a display name) and
  // the permission must hold for exactly that restaurant. Unknown -> denied.
  const allowedFor = (permission, restaurantId) => {
    const id = typeof restaurantId === "string" ? restaurantId.trim() : "";
    if (!id || !restaurants.some((item) => item.id === id)) return false;
    return can(permission, { restaurantId: id });
  };
  const notifyDenied = () =>
    setNotice({
      variant: "danger",
      text: tx("غير مصرّح لك بتنفيذ هذا الإجراء", "You are not allowed to do this", ar),
      detail: "",
    });

  const restaurant =
    restaurants.find((item) => item.id === selectedId) || restaurants[0];
  const menu = menus[restaurant?.id] || EMPTY_MENU;
  const actor =
    !isProvider
      ? tx("موظف LUD", "LUD employee", ar)
      : tx("فريق مزود التكامل", "Integration provider team", ar);

  const restaurantRequests = useMemo(
    () =>
      requests.filter(
        (request) =>
          (request.storeCode === restaurant?.id ||
            request.payload.restId === restaurant?.id) &&
          (isProvider ? request.status !== "draft" : true),
      ),
    [requests, restaurant, isProvider],
  );

  const allRestaurants = useMemo(
    () => [
      ...new Set(
        restaurants.map((item) =>
          String(item.name || "")
            .trim()
            .toLowerCase(),
        ),
      ),
    ],
    [restaurants],
  );

  const filteredRestaurants = useMemo(
    () =>
      restaurants.filter((item) => {
        const restaurantName = String(item.name || "")
          .trim()
          .toLowerCase();
        const currentMenu = menus[item.id] || { sections: [] };

        if (
          filters.restaurant.length > 0 &&
          !filters.restaurant.includes(restaurantName)
        ) {
          return false;
        }

        if (filters.section.length > 0) {
          const hasSection = currentMenu.sections.some((section) =>
            filters.section.includes(
              String(section.name || "")
                .trim()
                .toLowerCase(),
            ),
          );

          if (!hasSection) return false;
        }

        if (filters.item.length > 0) {
          const hasItem = currentMenu.sections.some((section) =>
            (section.items || []).some((menuItem) =>
              filters.item.includes(
                String(menuItem.name || "")
                  .trim()
                  .toLowerCase(),
              ),
            ),
          );

          if (!hasItem) return false;
        }

        return true;
      }),
    [restaurants, menus, filters],
  );

  useEffect(() => {
    if (!filteredRestaurants.length) return;

    const selectedExists = filteredRestaurants.some(
      (item) => item.id === selectedId,
    );

    if (!selectedExists) {
      setSelectedId(filteredRestaurants[0].id);
    }
  }, [filteredRestaurants, selectedId]);

  const filteredMenu = useMemo(() => {
    const sections = (menu.sections || [])
      .filter((section) => {
        if (!filters.section.length) return true;

        return filters.section.includes(
          String(section.name || "")
            .trim()
            .toLowerCase(),
        );
      })
      .map((section) => ({
        ...section,
        items: (section.items || []).filter((item) => {
          if (!filters.item.length) return true;

          return filters.item.includes(
            String(item.name || "")
              .trim()
              .toLowerCase(),
          );
        }),
      }))
      .filter((section) =>
        filters.item.length > 0 ? section.items.length > 0 : true,
      );

    return {
      ...menu,
      sections,
    };
  }, [menu, filters.section, filters.item]);

  const restaurantLogs = useMemo(
    () =>
      activity
        .filter((item) => item.restId === restaurant?.id)
        .map((item) => ({ ...item, date: fmtDate(item.rawDate, ar) })),
    [activity, restaurant, ar],
  );

  const closeFormModal = () => setFormModal(null);

  const openFormModal = (config) => {
    setFormModal({
      confirmText: tx("تأكيد", "Confirm", ar),
      confirmVariant: "primary",
      fields: [],
      initialValues: {},
      ...config,
    });
  };

  const log = (action, restId = restaurant.id) => {
    setActivity((current) => [
      {
        restId,
        createdAt: new Date().toISOString(),
        rawDate: new Date().toISOString(),
        actor,
        action,
      },
      ...current,
    ]);
  };

  const mutateRestaurant = (callback) => {
    setRestaurants((list) =>
      list.map((item) =>
        item.id === restaurant.id ? callback(cloneRestaurant(item)) : item,
      ),
    );
  };

  const addRequest = async (type, payload, status = "draft") => {
    // Requests are created from the connection / menu controls (ceo-level, per restaurant).
    if (!allowedFor("ludConnect.manage", restaurant?.id)) {
      notifyDenied();
      throw new Error(tx("غير مصرّح لك بتنفيذ هذا الإجراء", "You are not allowed to do this", ar));
    }
    const response = await createRequestAPI(token, {
      storeId: restaurant.id,
      type,
      status,
      branchId: payload.branchId || "",
      appId: payload.appId || "",
      payload,
      note: payload.note || "",
    });
    // The request exists now; a failed reload must not look like a failed send.
    await refreshOverview();
    return response.request;
  };

  const addCommentToRequest = (request, text) => {
    if (!text) return;
    request.comments = request.comments || [];
    request.comments.push({ date: new Date(), actor, text });
  };

  const addHistory = (request, status, label) => {
    request.history = request.history || [];
    request.history.push({ status, label, date: new Date() });
  };

  const onIssueRaw = (branchId, appId) => {
    const selectedBranch =
      restaurant.branches.find((branch) => branch.id === branchId) ||
      restaurant.branches[0];

    openFormModal({
      title: tx("الإبلاغ عن مشكلة تقنية", "Report a technical issue", ar),
      description: tx(
        "يصل هذا البلاغ مباشرة إلى الطرف الآخر دون مرحلة داخلية",
        "This report goes directly to the other party without an internal stage",
        ar,
      ),
      confirmText: tx("إرسال البلاغ", "Send report", ar),
      confirmVariant: "danger",
      initialValues: {
        branchId: selectedBranch?.id || "",
        appId: appId || "",
      },
      fields: [
        {
          name: "branchId",
          label: tx("الفرع", "Branch", ar),
          type: "select",
          required: true,
          options: restaurant.branches.map((branch) => ({
            value: branch.id,
            label: branch.name,
          })),
        },
        {
          name: "appId",
          label: tx("التطبيق (اختياري)", "Application (optional)", ar),
          type: "select",
          options: [
            { value: "", label: tx("— بدون تحديد —", "— Not specified —", ar) },
            ...APPS.map((app) => ({ value: app.id, label: app.name })),
          ],
        },
        {
          name: "description",
          label: tx("وصف المشكلة", "Issue description", ar),
          type: "textarea",
          placeholder: tx(
            "مثال: السعر لا يتحدّث في تطبيق كيتا رغم تأكيد الإرسال",
            "Example: the price does not update in Keeta despite confirming the send",
            ar,
          ),
          required: true,
        },
      ],
      onConfirm: async (values) => {
        const branch = restaurant.branches.find(
          (item) => item.id === values.branchId,
        );
        const app = APPS.find((item) => item.id === values.appId);
        await addRequest(
          "issue",
          {
            branchId: branch.id,
            branchName: branch.name,
            appId: values.appId || undefined,
            appName: app?.name,
            description: values.description.trim(),
          },
          "sent",
        );
        log(
          `أرسل بلاغ مشكلة${app ? ` على ${app.name}` : ""} للفرع "${branch.name}"`,
        );
        closeFormModal();
      },
    });
  };

  const onRestaurantFieldChange = (group, field, value) => {
    // POS credentials: integration partner only. Provider admin account: either side.
    const allowed =
      allowedFor("ludConnect.provider.act", restaurant?.id) ||
      (group !== "posSystem" && allowedFor("ludConnect.lud.act", restaurant?.id));
    if (!allowed) return notifyDenied();
    mutateRestaurant((item) => {
      if (group === "posSystem") item.posSystem[field] = value;
      else item[field] = value;
      return item;
    });
    const body =
      group === "posSystem"
        ? { posSystem: { ...restaurant.posSystem, [field]: value } }
        : { [field]: value };
    const storeId = restaurant.id;
    scheduleSave(`profile:${storeId}:${group}:${field}`, () =>
      updateProfileAPI(token, storeId, body),
    );
  };

  const onBranchField = (branchId, field, value) => {
    // Branch credentials are filled by the integration partner.
    if (!allowedFor("ludConnect.provider.act", restaurant?.id)) return notifyDenied();
    mutateRestaurant((item) => {
      const branch = item.branches.find((current) => current.id === branchId);
      branch[field] = value;
      if (branch.email && branch.pass && !branch.credAddedAt)
        branch.credAddedAt = new Date();
      return item;
    });
    const storeId = restaurant.id;
    scheduleSave(`branch:${storeId}:${branchId}:${field}`, () =>
      updateBranchAPI(token, storeId, branchId, { [field]: value }),
    );
  };

  const onAppField = (branchId, appId, field, value) => {
    if (!allowedFor("ludConnect.manage", restaurant?.id)) return notifyDenied();
    mutateRestaurant((item) => {
      const connection = item.branches.find(
        (current) => current.id === branchId,
      ).mergedApps[appId];
      connection[field] = value;
      connection.linkDataAt = new Date();
      return item;
    });
    const storeId = restaurant.id;
    const save = () =>
      upsertBranchAppAPI(token, storeId, branchId, appId, {
        [field]: value,
      });
    // Status comes from a dropdown: save immediately. Text fields autosave.
    if (field === "status") {
      save()
        .then(refreshOverview)
        .catch((error) =>
          reportError(
            error,
            tx("لم يتم حفظ التعديل", "The change was not saved", ar),
          ),
        );
    } else {
      scheduleSave(`app:${storeId}:${branchId}:${appId}:${field}`, save);
    }
  };

  const onAddAppRaw = (branchId, appId) =>
    runAction(`add-app:${branchId}:${appId}`, async () => {
      await upsertBranchAppAPI(token, restaurant.id, branchId, appId, {
        status: "new",
        appBranchName: "",
        credId: "",
      });
      mutateRestaurant((item) => {
        item.branches.find((branch) => branch.id === branchId).mergedApps[
          appId
        ] = {
          status: "new",
          appBranchName: "",
          credId: "",
        };
        return item;
      });
      log(`أضاف تطبيق ${APPS.find((app) => app.id === appId)?.name} للفرع`);
    });

  const onLinkRaw = (branchId, appId) =>
    runAction(`link:${branchId}:${appId}`, async () => {
      const branch = restaurant.branches.find((item) => item.id === branchId);
      const connection = branch.mergedApps[appId];
      await addRequest("link_app", {
        branchId,
        branchName: branch.name,
        appId,
        appName: APPS.find((app) => app.id === appId)?.name,
        credId: connection.credId,
        appBranchName: connection.appBranchName,
      });
      log(
        `أرسل طلب ربط تطبيق ${APPS.find((app) => app.id === appId)?.name} للفرع "${branch.name}"`,
      );
    });

  const openBranchActionModal = ({
    title,
    description,
    fieldLabel,
    fieldPlaceholder,
    confirmText,
    confirmVariant = "primary",
    required = false,
    onConfirm,
  }) => {
    openFormModal({
      title,
      description,
      confirmText,
      confirmVariant,
      fields: [
        {
          name: "comment",
          label: fieldLabel,
          type: "textarea",
          placeholder: fieldPlaceholder,
          required,
        },
      ],
      // Awaited so the modal stays open (and shows the error) if the request fails.
      onConfirm: async (values) => {
        await onConfirm(values.comment?.trim() || "");
        closeFormModal();
      },
    });
  };

  const onDisconnectRaw = (branchId, appId) => {
    const branch = restaurant.branches.find((item) => item.id === branchId);
    const app = APPS.find((item) => item.id === appId);
    openBranchActionModal({
      title: tx("طلب إيقاف ربط", "Disconnect request", ar),
      fieldLabel: tx(
        "سبب إيقاف الربط (اختياري)",
        "Reason for disconnecting (optional)",
        ar,
      ),
      confirmText: tx("إرسال الطلب", "Send request", ar),
      confirmVariant: "danger",
      onConfirm: async (comment) => {
        await addRequest(
          "disconnect_app",
          {
            branchId,
            branchName: branch.name,
            appId,
            appName: app?.name,
            note: comment,
          },
          "sent",
        );
        log(`طلب إيقاف ربط ${app?.name} للفرع "${branch.name}"`);
      },
    });
  };

  const onActivateRaw = (branchId) => {
    const branch = restaurant.branches.find((item) => item.id === branchId);
    openBranchActionModal({
      title: tx("طلب تفعيل الفرع", "Branch activation request", ar),
      fieldLabel: tx(
        "تعليق مع طلب تفعيل الفرع (اختياري)",
        "Comment with branch activation request (optional)",
        ar,
      ),
      confirmText: tx("إرسال الطلب", "Send request", ar),
      confirmVariant: "success",
      onConfirm: async (comment) => {
        const request = await addRequest(
          "activate_branch",
          { branchId, branchName: branch.name },
          "sent",
        );
        // The comment used to be attached to the API response object only, so
        // it was never saved. Store it as a real comment on the new request.
        const requestId = request?._id || request?.id;
        if (comment && requestId) {
          await addCommentAPI(token, requestId, comment);
          await refreshOverview();
        }
        log(`أرسل طلب تفعيل الفرع "${branch.name}"`);
      },
    });
  };

  // Handler-level authorization: connection actions are ceo-level only
  // ("ludConnect.manage") and bound to the selected restaurant, re-checked right
  // before they run (and again inside addRequest, after any confirmation dialog).
  const manageOnly = (action) => (...args) => {
    if (!allowedFor("ludConnect.manage", restaurant?.id)) {
      notifyDenied();
      return undefined;
    }
    return action(...args);
  };
  const onIssue = manageOnly(onIssueRaw);
  const onAddApp = manageOnly(onAddAppRaw);
  const onLink = manageOnly(onLinkRaw);
  const onDisconnect = manageOnly(onDisconnectRaw);
  const onActivate = manageOnly(onActivateRaw);
  const onCloseBranch = manageOnly((branchId) => closeBranchRaw(branchId));

  const closeBranchRaw = (branchId) => {
    const branch = restaurant.branches.find((item) => item.id === branchId);
    openBranchActionModal({
      title: tx("طلب إغلاق الفرع بالكامل", "Close the entire branch", ar),
      fieldLabel: tx(
        "سبب إغلاق الفرع بالكامل (اختياري)",
        "Reason for closing the entire branch (optional)",
        ar,
      ),
      confirmText: tx("إرسال الطلب", "Send request", ar),
      confirmVariant: "danger",
      onConfirm: async (comment) => {
        await addRequest(
          "close_branch",
          {
            branchId,
            branchName: branch.name,
            wholeBranch: true,
            note: comment,
          },
          "sent",
        );
        mutateRestaurant((item) => {
          item.branches.find(
            (current) => current.id === branchId,
          ).closeRequestedAt = new Date();
          return item;
        });
        log(`أرسل طلب إغلاق الفرع "${branch.name}" بالكامل`);
      },
    });
  };

  const openMenuRequestModal = ({
    scope,
    title,
    description,
    data = {},
    fields,
  }) => {
    if (!allowedFor("ludConnect.manage", restaurant?.id)) return notifyDenied();
    openFormModal({
      title,
      description,
      confirmText: tx("إرسال الطلب", "Send request", ar),
      fields,
      onConfirm: async (values) => {
        if (scope === "add") {
          await addRequest("menu_update", {
            scope,
            ...data,
            item: {
              name: values.name.trim(),
              desc: values.desc || "",
              price: Number(values.price) || 0,
              cal: Number(values.cal) || 0,
            },
          });
        } else {
          await addRequest("menu_update", {
            scope,
            comment: values.comment.trim(),
            ...data,
          });
        }
        closeFormModal();
      },
    });
  };

  // Which side of a request the user may act on, for the restaurant the request
  // belongs to (`storeCode`). Both permissions are checked against that
  // restaurant, never against whatever is currently selected.
  const requestSide = (request) => {
    const restaurantId = request?.storeCode || request?.payload?.restId;
    if (allowedFor("ludConnect.provider.act", restaurantId)) return "provider";
    if (allowedFor("ludConnect.lud.act", restaurantId)) return "lud";
    return null;
  };

  const onRequestAction = (id, action) => {
    const providerActions = [
      "accept",
      "clarify",
      "finish",
      "reject",
      "comment",
    ];
    const ludActions = [
      "send",
      "resend",
      "confirm",
      "reopen",
      "delete",
      "restore",
      "comment",
    ];
    const request = requests.find((item) => item.id === id);
    if (!request) return;

    // Permission and restaurant first; the per-side action lists stay as an
    // additional restriction on top of it.
    const side = requestSide(request);
    const allowedActions =
      side === "provider" ? providerActions : side === "lud" ? ludActions : [];

    if (!allowedActions.includes(action)) {
      if (!side) notifyDenied();
      return;
    }

    if (action === "delete") {
      setDeleteState({ loading: false, error: "" });
      setDeleteTarget(request);
      return;
    }

    if (action === "reject") {
      setRejectTarget(request);
      return;
    }

    if (action === "restore") {
      runAction(`restore:${id}`, async () => {
        if (requestSide(request) !== "lud") return notifyDenied();
        await restoreRequestAPI(token, id);
        setRequests((list) =>
          list.map((item) =>
            item.id === id
              ? { ...item, deleted: false, deletedAt: null }
              : item,
          ),
        );
        log(`تراجع عن حذف طلب "${request.type}"`);
      });
      return;
    }

    const actionConfig = {
      send: {
        title: tx("إرسال الطلب", "Send request", ar),
        label: "تعليق مع الإرسال (اختياري)",
        confirmText: "إرسال",
        required: false,
      },
      resend: {
        title: tx("إعادة الإرسال", "Resend", ar),
        label: "تعليق مع إعادة الإرسال (اختياري)",
        confirmText: tx("إعادة الإرسال", "Resend", ar),
        required: false,
      },
      accept: {
        title: tx("قبول الطلب", "Accept request", ar),
        label: "تعليق الموافقة (إلزامي)",
        confirmText: "قبول",
        confirmVariant: "success",
        required: true,
      },
      finish: {
        title: tx("تنفيذ مكتمل", "Execution complete", ar),
        label: "تعليق إنهاء التنفيذ (اختياري)",
        confirmText: tx("تنفيذ مكتمل", "Execution complete", ar),
        confirmVariant: "success",
        required: false,
      },
      confirm: {
        title: tx("تأكيد الإنجاز", "Confirm completion", ar),
        label: "تعليق التأكيد (إلزامي)",
        confirmText: tx("تأكيد الإنجاز", "Confirm completion", ar),
        confirmVariant: "success",
        required: true,
      },
      reopen: {
        title: tx("إعادة فتح الطلب", "Reopen request", ar),
        label: "سبب إعادة الفتح (اختياري)",
        confirmText: "إعادة فتح",
        required: false,
      },
      clarify: {
        title: tx("طلب توضيح", "Request clarification", ar),
        label: "ملاحظة التوضيح المطلوبة",
        confirmText: "إرسال",
        required: true,
      },
      comment: {
        title: tx("إضافة تعليق", "Add comment", ar),
        label: "أضف تعليق",
        confirmText: tx("إضافة", "Add", ar),
        required: true,
      },
    }[action];

    if (!actionConfig) return;

    openFormModal({
      title: actionConfig.title,
      confirmText: actionConfig.confirmText,
      confirmVariant: actionConfig.confirmVariant || "primary",
      fields: [
        {
          name: "comment",
          label: actionConfig.label,
          type: "textarea",
          required: actionConfig.required,
        },
      ],
      onConfirm: async (values) => {
        // The dialog may have been open for a while: check again right before the call.
        const currentSide = requestSide(requests.find((item) => item.id === id));
        if (currentSide !== side) {
          notifyDenied();
          closeFormModal();
          return;
        }
        const comment = values.comment?.trim() || "";
        if (action === "comment") await addCommentAPI(token, id, comment);
        else
          await transitionRequestAPI(
            token,
            id,
            action === "resend" ? "send" : action,
            comment,
          );
        setRequests((list) =>
          list.map((item) => {
            if (item.id !== id) return item;
            const next = clone(item);

            if (action === "comment") {
              addCommentToRequest(next, comment);
              return next;
            }

            if (action === "send" || action === "resend") {
              next.status = "sent";
              next.note = "";
              addHistory(
                next,
                "sent",
                action === "resend"
                  ? `أُعيد الإرسال بعد التوضيح${comment ? ` — ${comment}` : ""}`
                  : `تم الإرسال لمزود التكامل${comment ? ` — ${comment}` : ""}`,
              );
            }

            if (action === "accept") {
              next.status = "req_in_progress";
              addHistory(
                next,
                "req_in_progress",
                `قبل مزود التكامل الطلب — ${comment}`,
              );
            }

            if (action === "finish") {
              next.status = "pending_confirm";
              addHistory(
                next,
                "pending_confirm",
                `مزود التكامل أنهى التنفيذ — بانتظار تأكيد LUD${comment ? ` — ${comment}` : ""}`,
              );
            }

            if (action === "confirm") {
              next.status = "done";
              next.doneAt = new Date();
              addHistory(next, "done", `LUD أكّد الإنجاز — ${comment}`);
            }

            if (action === "reopen") {
              next.status = "req_in_progress";
              addHistory(
                next,
                "req_in_progress",
                `LUD أعاد فتح الطلب — التنفيذ غير مكتمل${comment ? ` — ${comment}` : ""}`,
              );
            }

            if (action === "clarify") {
              next.status = "clarify";
              next.note = comment;
              addHistory(next, "clarify", `طلب توضيح: ${comment}`);
            }

            if (comment) addCommentToRequest(next, comment);
            return next;
          }),
        );
        closeFormModal();
      },
    });
  };

  const handleDeleteRequest = async (id) => {
    const target = requests.find((item) => item.id === id);
    if (requestSide(target) !== "lud") {
      notifyDenied();
      setDeleteTarget(null);
      return;
    }
    setDeleteState({ loading: true, error: "" });
    try {
      await deleteRequestAPI(token, id);
    } catch (error) {
      console.error(error);
      setDeleteState({
        loading: false,
        error:
          error?.message ||
          tx("تعذّر حذف الطلب", "Could not delete the request", ar),
      });
      return;
    }
    const request = requests.find((item) => item.id === id);
    setRequests((list) =>
      list.map((item) =>
        item.id === id
          ? { ...item, deleted: true, deletedAt: new Date() }
          : item,
      ),
    );
    if (request) log(`حذف طلب "${request.type}"`);
    setDeleteState({ loading: false, error: "" });
    setDeleteTarget(null);
  };

  const handleRejectRequest = async (reason) => {
    if (!rejectTarget || rejecting) return;
    if (requestSide(requests.find((item) => item.id === rejectTarget.id)) !== "provider") {
      notifyDenied();
      setRejectTarget(null);
      return;
    }
    setRejecting(true);
    try {
      await transitionRequestAPI(token, rejectTarget.id, "reject", reason);
    } catch (error) {
      // Keep the modal open so the reason isn't lost; explain what happened.
      reportError(error, tx("تعذّر رفض الطلب", "Could not reject the request", ar));
      setRejecting(false);
      return;
    }
    setRequests((list) =>
      list.map((item) => {
        if (item.id !== rejectTarget.id) return item;
        const next = clone(item);
        next.status = "rejected";
        next.reason = reason;
        next.doneAt = new Date();
        addHistory(next, "rejected", `رُفض: ${reason}`);
        addCommentToRequest(next, reason);
        return next;
      }),
    );
    setRejecting(false);
    setRejectTarget(null);
  };

  const download = async (reference) => {
    if (!allowedFor("ludConnect.document.download", restaurant?.id)) return notifyDenied();
    try {
      const response = await getLudDocumentFileUrlAPI(token, restaurant.id, {
        kind: "organization",
        reference,
      });

      const fileUrl = response?.url || response?.data?.url;
      const originalName =
        response?.originalName ||
        response?.data?.originalName ||
        reference ||
        "file";

      const systemFileNames = {
        cr: "commercial-registration",
        iban: "iban-certificate",
        tax: "vat-certificate",
        vat: "vat-certificate",
        license: "business-license",
        address: "national-address",
        national_address: "national-address",
        productPhotos: "product-images",
        logo: "logo",
        menuInfo: "menu-information",
        restaurantMenu: "restaurant-menu",
        deliveryAppsMenu: "delivery-apps-menu",
        foodCostSheet: "food-cost-sheet",
      };

      const systemBaseName = systemFileNames[reference] || reference || "file";

      if (!fileUrl) {
        throw new Error("File URL was not returned");
      }

      const downloadResponse = await fetch(fileUrl);

      if (!downloadResponse.ok) {
        throw new Error(`Failed to download file: ${downloadResponse.status}`);
      }

      const blob = await downloadResponse.blob();
      const contentType = String(
        downloadResponse.headers.get("content-type") || blob.type || "",
      )
        .split(";")[0]
        .trim()
        .toLowerCase();

      const mimeExtensions = {
        "image/jpeg": "jpg",
        "image/jpg": "jpg",
        "image/png": "png",
        "image/webp": "webp",
        "image/gif": "gif",
        "image/svg+xml": "svg",
        "application/pdf": "pdf",
        "application/vnd.ms-excel": "xls",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          "xlsx",
        "application/zip": "zip",
        "application/x-zip-compressed": "zip",
      };

      const originalExtensionMatch =
        String(originalName).match(/\.([^./\\]+)$/);
      const originalExtension =
        originalExtensionMatch?.[1]?.toLowerCase() || "";
      const extension = mimeExtensions[contentType] || originalExtension;

      const downloadName = extension
        ? `${systemBaseName}.${extension}`
        : systemBaseName;

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      reportError(
        error,
        tx("تعذّر تحميل الملف", "Could not download the file", ar),
      );
    }
  };

  const dir = ar ? "rtl" : "ltr";
  const containerClass = `lud-connect-container ${ar ? "is-rtl" : "is-ltr"}`;

  if (loadState.status === "loading" && !restaurants.length) {
    return (
      <div className={containerClass} dir={dir}>
        <Loading />
      </div>
    );
  }

  if (loadState.status === "error" && !restaurants.length) {
    return (
      <div className={containerClass} dir={dir}>
        <div className="lud-connect__state">
          <NoticeBox
            variant="danger"
            title={tx(
              "تعذّر تحميل بيانات LUD Connect",
              "LUD Connect could not be loaded",
              ar,
            )}
          >
            {loadState.error && <p>{loadState.error}</p>}
            <Button
              variant="danger"
              appearance="outline"
              icon={<RefreshCcw size={15} />}
              onClick={initialLoad}
            >
              {tx("إعادة المحاولة", "Retry", ar)}
            </Button>
          </NoticeBox>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className={containerClass} dir={dir}>
        <div className="lud-connect__state">
          <NoticeBox
            variant="neutral"
            title={tx("لا توجد مطاعم مرتبطة بعد", "No restaurants yet", ar)}
          >
            {tx(
              "ستظهر المطاعم هنا بمجرد إضافتها إلى بوابة التكامل.",
              "Restaurants will appear here once they are added to the integration portal.",
              ar,
            )}
          </NoticeBox>
        </div>
      </div>
    );
  }

  const linkOpen = restaurantRequests.filter(
    (request) =>
      [
        "link_app",
        "disconnect_app",
        "activate_branch",
        "close_branch",
      ].includes(request.type) &&
      !["done", "rejected"].includes(request.status),
  ).length;

  const editOpen = restaurantRequests.filter(
    (request) =>
      ["menu_update", "issue"].includes(request.type) &&
      !["done", "rejected"].includes(request.status),
  ).length;

  const menuUpdateRequests = restaurantRequests.filter(
    (request) =>
      request.type === "menu_update" &&
      !request.deleted &&
      !["done", "rejected"].includes(request.status),
  );
  const totalBranches = restaurants.reduce(
    (total, item) => total + (item.branches?.length ?? 0),
    0,
  );

  return (
    <div className={containerClass} dir={dir}>
      <div className="lud-connect__content">
        {notice && (
          <div className="lud-connect__notice" role="alert">
            <NoticeBox variant={notice.variant} title={notice.text}>
              {notice.detail && <p>{notice.detail}</p>}
            </NoticeBox>
            <Button
              variant="neutral"
              appearance="ghost"
              size="sm"
              icon={<X size={16} />}
              aria-label={tx("إغلاق التنبيه", "Dismiss", ar)}
              title={tx("إغلاق التنبيه", "Dismiss", ar)}
              onClick={() => setNotice(null)}
            />
          </div>
        )}

        <div className="lud-connect__filters">
          <FilterUI
            fullWidth={false}
            label={ar ? "المطعم — الكل" : "Restaurant — All"}
            options={allRestaurants}
            selected={filters.restaurant}
            onChange={(vals) => handleFilterChange("restaurant", vals)}
          />
          <Button
            variant="warning"
            appearance="soft"
            onClick={handleClearFilters}
          >
            {tx("مسح الفلاتر", "Clear filters", ar)}
          </Button>
          <Button
            size="sm"
            variant="warning"
            appearance="filled"
            icon={<AlertTriangle size={15} />}
            onClick={() => onIssue()}
          >
            {tx("الإبلاغ عن مشكلة", "Report an issue", ar)}
          </Button>
        </div>
        <RestaurantStrip
          ar={ar}
          restaurants={filteredRestaurants}
          requests={requests}
          selectedId={restaurant.id}
          onSelect={setSelectedId}
        />

        <PortalStats
          restCounter={filteredRestaurants.length}
          branchesCounter={`${restaurant.branches.length}/${totalBranches}`}
          ar={ar}
          requests={requests}
          isProvider={isProvider}
        />

        <div className="lud-connect__layout">
          <main className="lud-connect__main">
            <RestaurantSummary
              ar={ar}
              restaurant={restaurant}
              menu={menu}
              isProvider={isProvider}
              onRestaurantFieldChange={onRestaurantFieldChange}
              onDownload={download}
              onError={reportError}
              linkOpenCount={linkOpen}
              editOpenCount={editOpen}
              refresh={refreshOverview}
            />

            <AdminApps ar={ar} restaurant={restaurant} />

            <Branches
              ar={ar}
              restaurant={restaurant}
              isProvider={isProvider}
              canManage={canManage}
              pendingActions={pendingActions}
              requests={requests}
              onBranchField={onBranchField}
              onAppField={onAppField}
              onAddApp={onAddApp}
              onLink={onLink}
              onDisconnect={onDisconnect}
              onIssue={onIssue}
              onActivate={onActivate}
              onClose={onCloseBranch}
            />

            <MenuPortal
              ar={ar}
              menu={filteredMenu}
              canManage={canManage}
              requests={menuUpdateRequests}
              onAddItem={(sectionId) => {
                const section = menu.sections.find(
                  (item) => item.id === sectionId,
                );
                // Items of the restaurant's active menus.
                const menuItems = menu.sections.flatMap(
                  (currentSection) => currentSection.items || [],
                );

                openFormModal({
                  title: tx("طلب إضافة صنف", "Add item request", ar),
                  description: `القسم: ${section?.name || ""}`,
                  confirmText: tx("إرسال الطلب", "Send request", ar),
                  fields: [
                    {
                      name: "itemId",
                      label: tx("الصنف المعتمد", "Approved item", ar),
                      type: "select",
                      required: true,
                      options: menuItems.map((item) => ({
                        value: item.id,
                        label: item.name,
                      })),
                    },
                  ],
                  onConfirm: async (values) => {
                    const selectedItem = menuItems.find(
                      (item) => item.id === values.itemId,
                    );
                    if (!selectedItem) return;
                    await addRequest("menu_update", {
                      scope: "add",
                      secId: sectionId,
                      itemId: selectedItem.id,
                      item: {
                        _id: selectedItem.id,
                        name: selectedItem.name,
                        desc: selectedItem.desc || "",
                        price: selectedItem.price || 0,
                        cal: selectedItem.cal || 0,
                      },
                    });
                    closeFormModal();
                  },
                });
              }}
              onUpdateItem={(item) =>
                openMenuRequestModal({
                  scope: "item",
                  title: tx("طلب تحديث صنف", "Item update request", ar),
                  description: item.name,
                  data: { itemId: item.id, itemName: item.name },
                  fields: [
                    {
                      name: "comment",
                      label: tx("التعليق", "Comment", ar),
                      type: "textarea",
                      placeholder: tx(
                        "مثال: غيّر السعر إلى 25 ريال",
                        "Example: change the price to 25 SAR",
                        ar,
                      ),
                      required: true,
                    },
                  ],
                })
              }
              onUpdateFull={() =>
                openMenuRequestModal({
                  scope: "full",
                  title: tx(
                    "طلب تحديث القائمة الكاملة",
                    "Full menu update request",
                    ar,
                  ),
                  description: restaurant.name,
                  fields: [
                    {
                      name: "comment",
                      label: tx("التعليق", "Comment", ar),
                      type: "textarea",
                      placeholder: tx(
                        "مثال: تحديث كامل للأسعار مرفق بالمستندات",
                        "Example: full price update attached with the documents",
                        ar,
                      ),
                      required: true,
                    },
                  ],
                })
              }
              onUpdateMulti={(ids) => {
                const allMenuItems = menu.sections.flatMap(
                  (section) => section.items,
                );
                const nameOf = (id) =>
                  allMenuItems.find((item) => item.id === id)?.name;
                openMenuRequestModal({
                  scope: "multi",
                  title: `طلب تحديث لـ${ids.length} صنف محدد`,
                  description: ids.map(nameOf).filter(Boolean).join("، "),
                  data: {
                    items: ids.map((id) => ({ id, name: nameOf(id) })),
                  },
                  fields: [
                    {
                      name: "comment",
                      label: tx("التعليق", "Comment", ar),
                      type: "textarea",
                      placeholder: tx(
                        "مثال: غيّر السعر إلى 25 ريال",
                        "Example: change the price to 25 SAR",
                        ar,
                      ),
                      required: true,
                    },
                  ],
                });
              }}
            />
          </main>

          <RequestsPanel
            ar={ar}
            requests={restaurantRequests}
            isProvider={isProvider}
            pendingActions={pendingActions}
            onAction={onRequestAction}
            logs={restaurantLogs}
          />
        </div>
      </div>

      <LudConnectFormModal
        open={Boolean(formModal)}
        {...formModal}
        onClose={closeFormModal}
      />

      {deleteTarget && (
        <DeleteModal
          id={deleteTarget.id}
          loading={deleteState.loading}
          error={deleteState.error}
          handleDelete={handleDeleteRequest}
          cancel={() => setDeleteTarget(null)}
        />
      )}

      <RejectModal
        open={Boolean(rejectTarget)}
        loading={rejecting}
        onConfirm={handleRejectRequest}
        onClose={() => setRejectTarget(null)}
      />
    </div>
  );
};

export default LudConnect;
